import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import React, { useEffect, useState } from 'react';
import { useDispatch, useIntl, useLocation, useSelector } from 'umi';
import { Button, Flex, Form, Space } from 'antd';
import styles from './index.less';
import { searchCustomersByContactRequest } from '@/services/api/customer';
import { LeadModel } from '@/typings/models/Lead';
import { processAPIResponseNow } from '@/utils/request';
import { ROUTE_PATHS } from '../../../../config/RoutePaths';
import { LEAD_VIEWS } from '@/components/Lead/constants';
import pick from 'lodash/pick';
import * as LeadSelectors from '@/selectors/leadSelectors';
import * as LeadStateSelectors from '@/selectors/leadStateSelectors';

import AddProjectRelatedLeads from '@/components/LeadSource/ManualLead/AddProjectRelatedLeads';
import CustomerForm from '@/components/LeadSource/ManualLead/CustomerForm';
import ProjectForm from '@/components/LeadSource/ManualLead/ProjectForm';
import {
  showCustomerExistsModal,
  SKIP_CUSTOMER_EXISTS_WARNING_KEY,
} from '@/components/LeadSource/ManualLead/CustomerExistsModal';
import { useCustomerRequiredFields } from '@/utils/hooks';

// Customer fields that should be nested under 'customer' in the payload
const CUSTOMER_FIELDS = ['customerId', 'firstname', 'lastname', 'email', 'phone', 'locale', 'gender'];

const AddManualLead: React.FC = () => {
  const dispatch = useDispatch();
  const customerRequiredFields = useCustomerRequiredFields();
  const intl = useIntl();
  const location = useLocation();
  const [customerForm] = Form.useForm();
  const [projectForm] = Form.useForm();

  const submitLoading = useSelector(
    (state: DefaultRootState) => state.loading.effects['leads/create'],
  );

  const leadStates = useSelector(LeadStateSelectors.selectStates);
  const addLeadDrawer = useSelector(LeadSelectors.selectAddLeadDrawer);
  const stateId = addLeadDrawer?.stateId || leadStates[0]?.id;


  // State for extra field values
  const [customerExtraFieldValues, setCustomerExtraFieldValues] = useState<any>({});
  const [projectExtraFieldValues, setProjectExtraFieldValues] = useState<any>({});

  // State for related leads
  const [foundCustomers, setFoundCustomers] = useState<any[]>([]);
  const [isCustomerFormDisabled, setIsCustomerFormDisabled] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);


  // Pre-fill form from payload if available
  useEffect(() => {
    if (addLeadDrawer) {
      const { email, phone, firstname, lastname } = addLeadDrawer;
      if (email || phone || firstname || lastname) {
        customerForm.setFieldsValue({
          email: email || '',
          phone: phone || '',
          firstname: firstname || '',
          lastname: lastname || '',
        });

        // If email or phone is provided, trigger search
        if (email || phone) {
          searchCustomers(email, phone);
        }
      }
    }
  }, [addLeadDrawer, customerForm]);

  // An agent carried by the payload (e.g. the calendar slot's resource) wins over
  // the field's own "assign to me" default.
  const payloadAssignee = (addLeadDrawer as { assignee?: string })?.assignee;
  useEffect(() => {
    if (!payloadAssignee) return;
    projectForm.setFieldsValue({ assignee_id: payloadAssignee });
  }, [payloadAssignee, projectForm]);

  useEffect(() => {
    if (stateId) {
      projectForm.setFieldsValue({
        lead_state_id: stateId,
      });
    }
  }, [stateId, projectForm]);


  // Search for existing customers by email or phone
  const searchCustomers = async (email?: string, phone?: string) => {
    if (!email && !phone) {
      setFoundCustomers([]);
      setIsCustomerFormDisabled(false);
      setIsSearchingCustomers(false);
      return;
    }

    setIsSearchingCustomers(true);

    try {
      const CustomerSearchResponse = await searchCustomersByContactRequest(email, phone);

      processAPIResponseNow(CustomerSearchResponse, {
        onSuccess(data) {
          if (data?.length > 0) {
            setFoundCustomers(data);
            const firstCustomer = data[0];

            // Check if user has opted to skip the warning
            const skipWarning = localStorage.getItem(SKIP_CUSTOMER_EXISTS_WARNING_KEY) === 'true';

            // Function to fill the form with customer data
            const fillCustomerForm = () => {
              customerForm.setFieldsValue({
                customerId: firstCustomer.id,
                firstname: firstCustomer.firstname || '',
                lastname: firstCustomer.lastname || '',
                email: firstCustomer.email || '',
                phone: firstCustomer.phone || '',
                locale: firstCustomer.locale || '',
                gender: firstCustomer.gender || '',
              });
              setIsCustomerFormDisabled(true);
            };

            if (skipWarning) {
              // If user opted to skip warning, fill form directly
              fillCustomerForm();
              setIsSearchingCustomers(false);
            } else {
              // Show modal with customer information
              showCustomerExistsModal({
                intl,
                customer: firstCustomer,
                onConfirm: () => {
                  fillCustomerForm();
                },
                onCancel: () => {
                  // Reset email and phone fields
                  customerForm.setFieldsValue({
                    email: '',
                    phone: '',
                  });
                  setFoundCustomers([]);
                  setIsCustomerFormDisabled(false);
                },
                onAfterClose: () => {
                  setIsSearchingCustomers(false);
                },
              });
            }
          } else {
            setFoundCustomers([]);
            setIsCustomerFormDisabled(false);
            setIsSearchingCustomers(false);
          }
        },
      }, 'data');

    } catch (error) {
      console.error('Error searching customers:', error);
      setFoundCustomers([]);
      setIsCustomerFormDisabled(false);
      setIsSearchingCustomers(false);
    }
  };

  // Handle email blur
  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value;
    if (email) {
      await searchCustomers(email, undefined);
    }
  };

  // Handle phone blur
  const handlePhoneBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    if (phone) {
      await searchCustomers(undefined, phone);
    }
  };

  // Handle using different email/phone
  const handleUseDifferentContact = () => {
    // Reset only email and phone fields
    customerForm.setFieldsValue({
      email: '',
      phone: '',
    });

    // Clear related leads state
    setFoundCustomers([]);

    // Re-enable the customer form
    setIsCustomerFormDisabled(false);
  };

  // Handle lead creation
  const onCreateLead = async () => {
    try {

      // Validate both forms
      await customerForm.validateFields();
      await projectForm.validateFields();

      // Get form values
      const customerFormData = customerForm.getFieldsValue();
      const projectFormData = projectForm.getFieldsValue();

      // Separate customer fields from the rest
      const customer = pick(customerFormData, CUSTOMER_FIELDS);

      // Transform extra fields to array format
      const customerExtraFieldsArray = Object.entries(customerExtraFieldValues)
        .map(([key, value]) => ({ key, value }))
        .filter((field) => field.value !== null && field.value !== undefined && field.value !== '');
      const projectExtraFieldsArray = Object.entries(projectExtraFieldValues)
        .map(([key, value]) => ({ key, value }))
        .filter((field) => field.value !== null && field.value !== undefined && field.value !== '');

      const customerPayload = isCustomerFormDisabled
        ? { id: customer.customerId }
        : {
          ...customer,
          ...(customerExtraFieldsArray.length > 0
            ? { extra_fields: customerExtraFieldsArray }
            : {}),
        };
      // Build the payload
      const payload = {
        intl,
        data: {
          customer: customerPayload,
          ...projectFormData,
          ...(projectExtraFieldsArray.length > 0 ? { extra_fields: projectExtraFieldsArray } : {}),
        },
      };

      // Dispatch create action
      dispatch({
        type: 'leads/create',
        payload,
        then: (createdLeads: LeadModel[]) => {

          // Refresh leads based on current route
          switch (location.pathname) {
            case ROUTE_PATHS.LEADS_BOARD:
              dispatch({
                type: 'leads/fetch',
                payload: {
                  view: LEAD_VIEWS.KANBAN,
                },
              });
              break;
            case ROUTE_PATHS.LEADS:
              dispatch({
                type: 'leads/fetch',
                payload: {
                  view: LEAD_VIEWS.TABLE,
                },
              });
              break;
          }

          if (addLeadDrawer?.onCreateLead) {
            addLeadDrawer.onCreateLead(createdLeads);
          } else {
            dispatch({
              type: 'leads/toggleLeadDetailsDrawer',
              payload: {
                lead: createdLeads[0],
              },
            });
          }

        },
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <div className={styles.drawerContainer}>
      <Space style={{ width: '100%' }} direction={'vertical'}>
        {/* Related Projects InfoCard */}
        <AddProjectRelatedLeads foundCustomers={foundCustomers} onUseDifferentContact={handleUseDifferentContact} />

        {/* Customer Information Card - Blue */}
        <CustomerForm
          form={customerForm}
          isCustomerFormDisabled={isCustomerFormDisabled}
          isSearchingCustomers={isSearchingCustomers}
          customerRequiredFields={customerRequiredFields}
          onEmailBlur={handleEmailBlur}
          onPhoneBlur={handlePhoneBlur}
          onCustomerExtraFieldValuesChange={setCustomerExtraFieldValues}
        />

        {/* Project Information Card - Purple */}
        <ProjectForm
          form={projectForm}
          onProjectExtraFieldValuesChange={setProjectExtraFieldValues}
        />

        {/* Submit Button */}
        <Flex justify={'flex-end'}>
          <Button
            data-testid="create-lead-submit-btn"
            type="primary"
            size="large"
            loading={submitLoading}
            onClick={onCreateLead}
            style={{ marginTop: 16 }}
          >
            <F entity={INTL_ENTITY.LEAD} action={INTL_ACTION.CREATE} />
          </Button>
        </Flex>
      </Space>
    </div>
  );
};

export default AddManualLead;
